import React from "react";
import countriesLib from "i18n-iso-countries";
import enLocale from "i18n-iso-countries/langs/en.json";

// Register locale once at module load
countriesLib.registerLocale(enLocale as any);

interface CountrySelectProps {
  value: string;
  onChange: (value: string) => void;
  baseFieldStyle: (field: string, hasValue: boolean) => React.CSSProperties;
  fieldTextStyle: React.CSSProperties;
  errors: Record<string, string>;
  touched: Record<string, boolean>;
  focused: string | null;
  setFocused: (field: string | null) => void;
  setAndValidate: (field: string, value: string) => void;
  setTouched: (touched: Record<string, boolean>) => void;
}

type Country = { code: string; name: string };

const CountrySelect: React.FC<CountrySelectProps> = ({
  value,
  onChange,
  baseFieldStyle,
  fieldTextStyle,
  errors,
  touched,
  focused,
  setFocused,
  setAndValidate,
  setTouched,
}) => {
  const countries: Country[] = React.useMemo(() => {
    const names = countriesLib.getNames("en", { select: "official" }) as Record<string, string>;
    return Object.entries(names)
      .map(([code, name]) => ({ code, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, []);

  const hasError = Boolean(errors.country && touched.country);
  const hasValue = Boolean(value);

  return (
    <div className="md:col-span-2 relative">
      <label htmlFor="country" className="sr-only">Country</label>
      <select
        id="country"
        name="country"
        autoComplete="country-name"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          if (touched.country) setAndValidate("country", e.target.value);
        }}
        onBlur={() => {
          setTouched({ ...touched, country: true });
          setAndValidate("country", value);
          setFocused(focused === "country" ? null : focused);
        }}
        onFocus={() => setFocused("country")}
        required
        className={`w-full appearance-none rounded border bg-transparent px-4 py-3 pr-10 text-white outline-none focus:border-white/60 h-[46px] md:h-auto`}
        style={{
          ...baseFieldStyle("country", hasValue),
          fontSize: '16px'
        }}
        aria-invalid={hasError}
        aria-describedby={hasError ? "country-error" : undefined}
      >
        <option value="" className="bg-[#0b0f17]">
          Select Country*
        </option>
        {countries.map((c) => (
          <option key={c.code} value={c.code} className="bg-[#0b0f17]">
            {c.name}
          </option>
        ))}
      </select>
      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-white/70">
        <svg
          width="14"
          height="8"
          viewBox="0 0 14 8"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M1 1L7 7L13 1"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
      {hasError && (
        <p
          id="country-error"
          role="alert"
          className="text-white mt-1"
          style={{
            ...fieldTextStyle,
            fontSize: 14,
            lineHeight: "140%",
            textShadow:
              "0px 0px 11.96px rgba(255,0,0,0.5), 0px 0px 23.92px #FF0000",
          }}
        >
          * {errors.country}
        </p>
      )}
    </div>
  );
};

export default CountrySelect;
